import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { AuthService } from "./generated-api/services/AuthService";
import { UnverifiedUsersService } from "./generated-api/services/UnverifiedUsersService";
import { EmailPayload } from "./generated-api/models/EmailPayload";
import { EmailOtpCodePayload } from "./generated-api/models/EmailOtpCodePayload";
import {
  ApiError,
  CancelablePromise,
  EmailCaptchaPayload,
  OpenAPI,
  GeoService,
  UserService,
  ProfileUpdateRequest,
  PhotosService,
  Body_upload_photo_api_photos_upload_post,
  VerificationService,
  Body_upload_video_api_verification_upload_post,
  ProfileService,
  ExploreService,
  BlockService,
  FavoriteService,
  UnreadsService,
  SettingsService,
  UpdateAccountSettings,
  FavoriteSortBy,
  AcceptTermsPayload,
  ReportService,
  ReportUserPayload,
  SearchService,
  ProfileSearchFilters,
  ProfileSearchResultResponse,
  MessagesService,
  NewMessage,
  ConversationResponse,
  ContactMessage,
  IntroUpdateRequest,
  MessageType,
  ConversationMessages,
  MessageResponse,
  SubscriptionService,
  SupportService,
  PushSubscriptionPayload,
  PushNotificationsService,
  PushUnsubscribePayload,
  IntentPayload,
  PaymentAttemptPayload,
  ContactType,
  TokenPayload,
  ProfileViewResponse,
  PrivatePhotoStatus,
  ReferralsService,
  EmailOtpCodeFingerprintPayload,
  StripeSessionIdPayload,
  SubscriptionStatus,
  DeleteUserPayload,
  AiMatchingService,
} from "./generated-api";
import { useAuthStore } from "./stores/authStore";
import { useCallback, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { withSentryMutation } from "./lib/sentryUtils";
import { useNavigate } from "react-router-dom";
import { tryRefreshingToken } from "./components/auth/tryRefreshingToken";
import {
  nativeCompleteLogin,
  nativeGoogleLogin,
  nativeAppleLogin,
  nativeLogout,
} from "./components/auth/nativeLoginService";
import { Capacitor } from "@capacitor/core";
import { FcmPushSubscriptionPayload } from "./generated-api/models/FcmPushSubscriptionPayload";

OpenAPI.BASE = import.meta.env.VITE_API_BASE_URL;
OpenAPI.WITH_CREDENTIALS = true;

if (Capacitor.isNativePlatform()) {
  OpenAPI.HEADERS = {
    ...OpenAPI.HEADERS,
    "x-pulse-client": "ios-capacitor",
  };
}

const TWENTY_MINUTES = 20 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const THREE_MINUTES = 3 * 60 * 1000;
const ONE_MINUTE = 60 * 1000;
const TEN_SECONDS = 10000;
const FIVE_SECONDS = 5000;
const TWO_SECONDS = 2000;
const TWENTY_FIVE_SECONDS = 25000;

const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

export const setOpenAPIToken = (token: string | null) => {
  if (token) {
    OpenAPI.HEADERS = {
      ...OpenAPI.HEADERS,
      Authorization: `Bearer ${token}`,
    };
  } else {
    if (OpenAPI.HEADERS && "Authorization" in OpenAPI.HEADERS) {
      delete OpenAPI.HEADERS.Authorization;
    }
  }
};

export const refreshOnFailure = async <T,>(
  apiCall: () => CancelablePromise<T>,
  accessToken: string | null,
  setToken: (accessToken: string) => void,
  clearToken: () => void,
): Promise<T> => {
  try {
    return await apiCall();
  } catch (error) {
    const err = error as ApiError;
    if (err.status === 401 && accessToken) {
      await tryRefreshingToken(setToken, clearToken);
      return await apiCall();
    }
    throw error;
  }
};

function useNamedMutation<TData, TError, TVariables, TContext>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    mutationKey: [string];
  },
) {
  const [name] = options.mutationKey;
  const wrappedOnError = withSentryMutation(options.onError);

  return useMutation({
    ...options,
    onMutate: async (variables) => {
      const userContext = await options.onMutate?.(variables);
      return {
        ...(userContext ?? {}),
        name,
      } as TContext & { name: string };
    },
    onError: wrappedOnError,
  });
}

const useAuthApiCallMutation = <T, U, C = unknown>({
  mutationKey,
  apiFunction,
  onSuccess,
  onMutate,
  onError,
}: {
  mutationKey: [string];
  apiFunction: (data: U) => CancelablePromise<T>;
  onSuccess?: (data: T, variables: U) => void; // Updated type signature here
  onMutate?: (variables: U) => Promise<C> | C;
  onError?: (error: Error, variables: U, context: C | undefined) => void;
}): UseMutationResult<T, Error, U, C> => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useNamedMutation<T, Error, U, C>({
    mutationKey,
    mutationFn: async (data: U) =>
      refreshOnFailure(
        () => apiFunction(data),
        accessToken,
        setToken,
        clearToken,
      ),
    onSuccess: (data, variables) => {
      // Now receiving both parameters
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
    onMutate,
    onError: withSentryMutation(onError),
  });
};

const useAuthApiCallQuery = <T,>({
  queryKey,
  apiFunction,
  ...queryOptions
}: {
  queryKey: string[];
  apiFunction: () => CancelablePromise<T>;
} & Omit<UseQueryOptions<T, Error, T>, "queryKey" | "queryFn">): UseQueryResult<
  T,
  Error
> => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useQuery<T, Error>({
    queryKey,
    queryFn: () =>
      refreshOnFailure(apiFunction, accessToken, setToken, clearToken),
    ...queryOptions,
  });
};

const useAuthApiCallQueryWithParams = <T, P>({
  queryKey,
  apiFunction,
  params,
  ...queryOptions // Add spread for additional options
}: {
  queryKey: string[];
  apiFunction: (data: P) => CancelablePromise<T>;
  params: P;
} & Omit<UseQueryOptions<T, Error, T>, "queryKey" | "queryFn">): UseQueryResult<
  T,
  Error
> => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useQuery<T, Error>({
    queryKey,
    queryFn: () =>
      refreshOnFailure(
        () => apiFunction(params),
        accessToken,
        setToken,
        clearToken,
      ),
    ...queryOptions, // Spread the options to useQuery
  });
};

const updateSearchResultsUserState = (
  queryClient: QueryClient,
  userId: string,
  updates: Partial<
    Pick<ProfileSearchResultResponse, "has_messaged" | "is_favorite">
  >,
) => {
  // Get the infinite query data
  const searchData = queryClient.getQueriesData<
    InfiniteData<{ items: ProfileSearchResultResponse[] }>
  >({
    queryKey: ["searchResults"],
  });

  searchData.forEach(([queryKey, data]) => {
    if (!data) return;

    // Update each page's items
    const updatedPages = data.pages.map((page) => ({
      ...page,
      items: page.items.map((profile) =>
        profile.user_id === userId ? { ...profile, ...updates } : profile,
      ),
    }));

    // Update the query data
    queryClient.setQueryData(queryKey, {
      ...data,
      pages: updatedPages,
    });
  });
};

const updateFavoriteResultsMessagedState = (
  queryClient: QueryClient,
  userId: string,
  updates: Partial<Pick<ProfileSearchResultResponse, "has_messaged">>,
) => {
  const favoritesData = queryClient.getQueriesData<
    InfiniteData<{ items: ProfileSearchResultResponse[] }>
  >({
    queryKey: ["listFavorites"],
  });

  favoritesData.forEach(([queryKey, data]) => {
    if (!data) return;
    const updatedPages = data.pages.map((page) => ({
      ...page,
      items: page.items.map((profile) =>
        profile.user_id === userId ? { ...profile, ...updates } : profile,
      ),
    }));

    // Update the query data
    queryClient.setQueryData(queryKey, {
      ...data,
      pages: updatedPages,
    });
  });
};

const updateFavoritedMeResultsUserState = (
  queryClient: QueryClient,
  userId: string,
  updates: Partial<
    Pick<ProfileSearchResultResponse, "has_messaged" | "is_favorite">
  >,
) => {
  const favoritedMeData = queryClient.getQueriesData<
    InfiniteData<{ items: ProfileSearchResultResponse[] }>
  >({
    queryKey: ["listFavoritedMe"],
  });

  favoritedMeData.forEach(([queryKey, data]) => {
    if (!data) return;
    const updatedPages = data.pages.map((page) => ({
      ...page,
      items: page.items.map((profile) =>
        profile.user_id === userId ? { ...profile, ...updates } : profile,
      ),
    }));

    // Update the query data
    queryClient.setQueryData(queryKey, {
      ...data,
      pages: updatedPages,
    });
  });
};

const updateCounterpartyUserState = (
  queryClient: QueryClient,
  userId: string,
  updates: Partial<
    Pick<
      ProfileViewResponse,
      "has_messaged" | "is_favorite" | "my_photos_share_status"
    >
  >,
) => {
  queryClient.setQueryData<ProfileViewResponse>(
    ["getProfileView", userId],
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        ...updates,
      };
    },
  );
};

// User Auth

export const useInitiateLogin = () => {
  return useNamedMutation({
    mutationKey: ["initiateLogin"],
    mutationFn: (email: EmailPayload) =>
      AuthService.initiateLoginApiPublicAuthInitiateLoginPost(email),
  });
};

export const useCompleteLogin = () => {
  return useNamedMutation({
    mutationKey: ["completeLogin"],
    mutationFn: (data: EmailOtpCodeFingerprintPayload) =>
      Capacitor.isNativePlatform()
        ? nativeCompleteLogin(data)
        : AuthService.completeLoginApiPublicAuthCompleteLoginPost(data),
  });
};

export const useResendOtp = () => {
  return useNamedMutation({
    mutationKey: ["resendOtp"],
    mutationFn: (email: EmailPayload) =>
      AuthService.resendOtpApiPublicAuthResendOtpPost(email),
  });
};

export const useLoginWithGoogle = () => {
  return useNamedMutation({
    mutationKey: ["loginWithGoogle"],
    mutationFn: (data: TokenPayload) =>
      Capacitor.isNativePlatform()
        ? nativeGoogleLogin(data)
        : AuthService.googleLoginApiPublicAuthGoogleLoginPost(data),
  });
};

export const useLoginWithApple = () => {
  return useNamedMutation({
    mutationKey: ["loginWithApple"],
    mutationFn: (data: TokenPayload) =>
      Capacitor.isNativePlatform()
        ? nativeAppleLogin(data)
        : AuthService.appleLoginApiPublicAuthAppleLoginPost(data),
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const { clearToken } = useAuthStore();
  const navigate = useNavigate();

  return useNamedMutation({
    mutationKey: ["logout"],
    mutationFn: () =>
      Capacitor.isNativePlatform()
        ? nativeLogout()
        : AuthService.logoutApiAuthLogoutPost(),
    onMutate: async () => {
      await queryClient.cancelQueries();
    },
    onError: withSentryMutation(),
    onSettled: () => {
      clearToken();
      queryClient.clear();
      sessionStorage.clear();
      navigate("/");
    },
  });
};

// Unverified Users

export const useCreateUnverifiedUser = () => {
  return useNamedMutation({
    mutationKey: ["createUnverifiedUser"],
    mutationFn: (data: EmailCaptchaPayload) =>
      UnverifiedUsersService.createUnverifiedUserApiPublicUnverifiedCreatePost(
        data,
      ),
  });
};

export const useVerifyUser = () => {
  return useNamedMutation({
    mutationKey: ["verifyUser"],
    mutationFn: (data: EmailOtpCodeFingerprintPayload) =>
      UnverifiedUsersService.verifyUserApiPublicUnverifiedVerifyPost(data),
  });
};

export const useResendOtpUnverified = () => {
  return useNamedMutation({
    mutationKey: ["resendOtpUnverified"],
    mutationFn: (email: EmailPayload) =>
      UnverifiedUsersService.resendOtpApiPublicUnverifiedResendOtpPost(email),
  });
};

// User

export const useGetUserProfile = () => {
  return useAuthApiCallQuery({
    queryKey: ["getUserProfile"],
    apiFunction: () => UserService.getProfileApiUserProfileGet(),
    gcTime: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["updateUserProfile"],
    apiFunction: (data: ProfileUpdateRequest) =>
      UserService.updateProfileApiUserProfilePatch(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["getUserProfile"] });
      void queryClient.invalidateQueries({ queryKey: ["getProfileViewSelf"] });
    },
  });
};

export const useUpdateIntro = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["updateIntro"],
    apiFunction: (data: IntroUpdateRequest) =>
      UserService.updateIntroApiUserIntroPost(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["getUserProfile"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { clearToken } = useAuthStore();
  return useAuthApiCallMutation({
    mutationKey: ["deleteUser"],
    apiFunction: (data: DeleteUserPayload) =>
      UserService.deleteUserApiUserDeleteDelete(data),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      queryClient.clear();
      clearToken();
    },
  });
};

// Geo

export const useGetLanguages = () => {
  return useAuthApiCallQuery({
    queryKey: ["getLanguages"],
    apiFunction: () => GeoService.getLanguagesApiGeoLanguagesGet(),
    gcTime: ONE_WEEK,
    staleTime: THREE_DAYS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useGetCountries = () => {
  return useAuthApiCallQuery({
    queryKey: ["getCountries"],
    apiFunction: () => GeoService.getCountriesApiGeoCountriesGet(),
    gcTime: ONE_WEEK,
    staleTime: THREE_DAYS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

// Photos

export const useGetUserPhotos = () => {
  return useAuthApiCallQuery({
    queryKey: ["getUserPhotos"],
    apiFunction: () => PhotosService.userPhotosApiPhotosListGet(),
    gcTime: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
};

export const useDeletePhoto = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["deletePhoto"],
    apiFunction: (orderId: number) =>
      PhotosService.deletePhotoApiPhotosDeleteDelete(orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUserPhotos"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["getProfileViewSelf"],
      });
    },
  });
};

export const useUploadPhoto = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["uploadPhoto"],
    apiFunction: (data: Body_upload_photo_api_photos_upload_post) =>
      PhotosService.uploadPhotoApiPhotosUploadPost(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUserPhotos"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["getProfileViewSelf"],
      });
    },
  });
};

export const useCompletedUpload = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["completedUpload"],
    apiFunction: () =>
      PhotosService.completedUploadApiPhotosCompletedUploadPost(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUserProfile"],
      });
    },
  });
};

// Facebook pixel tracking
declare function fbq(
  method: "track" | "trackCustom" | "init",
  eventName?: string,
  parameters?: Record<string, unknown>,
  options?: Record<string, unknown>,
): void;

function getCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="))
    ?.split("=")[1];
}

// Face verification
export const useUploadVideo = () => {
  const queryClient = useQueryClient();
  const fbEventId = crypto.randomUUID();

  const fbp = getCookie("_fbp") ?? undefined;
  const fbc = getCookie("_fbc") ?? undefined;

  return useAuthApiCallMutation({
    mutationKey: ["uploadVideo"],
    apiFunction: (data: Body_upload_video_api_verification_upload_post) =>
      VerificationService.uploadVideoApiVerificationUploadPost({
        ...data,
        fb_event_id: fbEventId,
        fb_fbc: fbc,
        fb_fbp: fbp,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUserProfile"],
      });
      fbq("track", "CompleteRegistration", {}, { eventID: fbEventId }); // Facebook pixel tracking
    },
  });
};

export const useUploadedVideo = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["useUploaded"],
    apiFunction: () =>
      VerificationService.uploadedSkipApiVerificationUploadedSkipPost(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUserProfile"],
      });
    },
  });
};

// Profile views

export const useGetProfileView = (userId: string) => {
  return useAuthApiCallQueryWithParams({
    queryKey: ["getProfileView", userId],
    apiFunction: () => ProfileService.profileApiProfileUserIdGet(userId),
    params: userId,
  });
};

export const useGetProfileViewSelf = () => {
  return useAuthApiCallQuery({
    queryKey: ["getProfileViewSelf"],
    apiFunction: () => ProfileService.selfProfileApiProfileSelfGet(),
  });
};

// Explore

export const useInfiniteExplore = () => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["explore"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          ExploreService.exploreApiExploreGet(pageParam as string | undefined),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    gcTime: Infinity,
    staleTime: Infinity,
  });
};

export const usePrefetchData = (enabled: boolean) => {
  const { accessToken, setToken, clearToken } = useAuthStore();
  const queryClient = useQueryClient();
  if (!enabled) return;
  const savedTabStr = sessionStorage.getItem("favoritesSorting");
  const savedTab: FavoriteSortBy =
    savedTabStr === FavoriteSortBy.RECENTLY_ACTIVE
      ? FavoriteSortBy.RECENTLY_ACTIVE
      : FavoriteSortBy.LAST_FAVORITED;

  const hasFavoritesData = queryClient.getQueryData([
    "listFavorites",
    savedTab,
  ]);
  if (!hasFavoritesData) {
    void queryClient.prefetchInfiniteQuery({
      queryKey: ["listFavorites", savedTab],
      queryFn: ({ pageParam }) =>
        refreshOnFailure(
          () =>
            FavoriteService.listFavoritesApiFavoriteListGet(
              savedTab,
              pageParam as string | undefined,
            ),
          accessToken,
          setToken,
          clearToken,
        ),
      initialPageParam: null as string | null,
    });
  }

  const hasRecentSearchData = queryClient.getQueryData(["getRecentSearch"]);
  if (!hasRecentSearchData) {
    void queryClient.prefetchQuery({
      queryKey: ["getRecentSearch"],
      queryFn: () =>
        refreshOnFailure(
          () => SearchService.recentSearchApiSearchRecentGet(),
          accessToken,
          setToken,
          clearToken,
        ),
    });
  }

  const hasConversationsData = queryClient.getQueryData(["listConversations"]);
  if (!hasConversationsData) {
    void queryClient.prefetchInfiniteQuery({
      queryKey: ["listConversations"],
      queryFn: ({ pageParam }) =>
        refreshOnFailure(
          () =>
            MessagesService.listConversationsApiMessagesConversationsGet(
              pageParam as string | undefined,
            ),
          accessToken,
          setToken,
          clearToken,
        ),
      initialPageParam: null as string | null,
    });
  }

  const hasSubscriptionData = queryClient.getQueryData([
    "getActiveSubscription",
  ]);
  if (!hasSubscriptionData) {
    void queryClient.prefetchQuery({
      queryKey: ["getActiveSubscription"],
      queryFn: () =>
        refreshOnFailure(
          () => SubscriptionService.getSubscriptionApiSubscriptionActiveGet(),
          accessToken,
          setToken,
          clearToken,
        ),
    });
  }
};

//  Block

export const useBlockUser = () => {
  const queryClient = useQueryClient();

  const updateSearchResults = (userId: string) => {
    // Get the infinite query data
    const searchData = queryClient.getQueriesData<
      InfiniteData<{ items: ProfileSearchResultResponse[] }>
    >({
      queryKey: ["searchResults"],
    });

    searchData.forEach(([queryKey, data]) => {
      if (!data) return;
      // Update each page's items by filtering out blocked profiles
      const updatedPages = data.pages.map((page) => ({
        ...page,
        items: page.items.filter((profile) => profile.user_id !== userId),
      }));

      // Update the query data
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: updatedPages,
      });
    });
  };

  const updateExploreResults = (userId: string) => {
    // Get the infinite query data for explore feed
    const exploreData = queryClient.getQueriesData<
      InfiniteData<{ items: ProfileSearchResultResponse[] }>
    >({
      queryKey: ["explore"],
    });

    exploreData.forEach(([queryKey, data]) => {
      if (!data) return;
      // Update each page's items by filtering out blocked profiles
      const updatedPages = data.pages.map((page) => ({
        ...page,
        items: page.items.filter((profile) => profile.user_id !== userId),
      }));

      // Update the query data
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: updatedPages,
      });
    });
  };

  const updateFavoriteResults = (userId: string) => {
    // Get the infinite query data for both favorites lists
    ["listFavorites", "listFavoritedMe"].forEach((queryKeyPrefix) => {
      // Get all queries that start with this prefix (to handle different sort options)
      const favoritesData = queryClient.getQueriesData<
        InfiniteData<{ items: ProfileSearchResultResponse[] }>
      >({
        queryKey: [queryKeyPrefix],
        exact: false,
      });

      favoritesData.forEach(([queryKey, data]) => {
        if (!data) return;
        // Update each page's items by filtering out blocked profiles
        const updatedPages = data.pages.map((page) => ({
          ...page,
          items: page.items.filter((profile) => profile.user_id !== userId),
        }));

        // Update the query data
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: updatedPages,
        });
      });
    });
  };

  return useAuthApiCallMutation({
    mutationKey: ["blockUser"],
    apiFunction: (userId: string) =>
      BlockService.blockUserApiBlockUserIdPost(userId),
    onSuccess: (_, variables) => {
      updateSearchResults(variables);
      updateExploreResults(variables);
      updateFavoriteResults(variables);
      void queryClient.invalidateQueries({
        queryKey: ["listConversations"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["listMessages", variables],
      });
    },
  });
};

//  Report

export const useReportUser = () => {
  return useAuthApiCallMutation({
    mutationKey: ["reportUser"],
    apiFunction: (payload: ReportUserPayload) =>
      ReportService.reportUserApiReportPost(payload),
  });
};

//  Private Photos

export const useSharePrivatePhotos = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["sharePrivatePhotos"],
    apiFunction: (userId: string) =>
      ProfileService.sharePhotosApiProfileUserIdSharePhotosPost(userId),
    onSuccess: (_, variables) => {
      updateSearchResultsUserState(queryClient, variables, {
        has_messaged: true,
      });
      updateFavoriteResultsMessagedState(queryClient, variables, {
        has_messaged: true,
      });
      updateFavoritedMeResultsUserState(queryClient, variables, {
        has_messaged: true,
      });
      updateCounterpartyUserState(queryClient, variables, {
        my_photos_share_status: PrivatePhotoStatus.SHARED,
      });
    },
  });
};

export const useUnsharePrivatePhotos = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["unsharePrivatePhotos"],
    apiFunction: (userId: string) =>
      ProfileService.unsharePhotosApiProfileUserIdUnsharePhotosPost(userId),
    onSuccess: (_, variables) => {
      updateCounterpartyUserState(queryClient, variables, {
        my_photos_share_status: PrivatePhotoStatus.HIDDEN,
      });
    },
  });
};

export const useRequestPrivatePhotos = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["requestPrivatePhotos"],
    apiFunction: (userId: string) =>
      ProfileService.requestPhotosApiProfileUserIdRequestPhotosPost(userId),
    onSuccess: (_, variables) => {
      updateSearchResultsUserState(queryClient, variables, {
        has_messaged: true,
      });
      updateFavoriteResultsMessagedState(queryClient, variables, {
        has_messaged: true,
      });
      updateFavoritedMeResultsUserState(queryClient, variables, {
        has_messaged: true,
      });
      void queryClient.invalidateQueries({
        queryKey: ["getProfileView", variables],
      });
    },
  });
};

// Favorites

export const useInfiniteFavorites = (sortBy: FavoriteSortBy) => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["listFavorites", sortBy],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          FavoriteService.listFavoritesApiFavoriteListGet(
            sortBy,
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    gcTime: FIVE_MINUTES,
    staleTime: ONE_MINUTE,
  });
};

export const useInfiniteListFavoritedMe = (sortBy: FavoriteSortBy) => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["listFavoritedMe", sortBy],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          FavoriteService.listFavoritedMeApiFavoriteListFavoritedMeGet(
            sortBy,
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    gcTime: TWENTY_FIVE_SECONDS,
    staleTime: TEN_SECONDS,
  });
};

export const useFavoriteUser = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["favoriteUser"],
    apiFunction: (userId: string) =>
      FavoriteService.favoriteUserApiFavoriteUserIdPost(userId),
    onSuccess: (_, variables) => {
      updateSearchResultsUserState(queryClient, variables, {
        is_favorite: true,
      });
      updateFavoritedMeResultsUserState(queryClient, variables, {
        is_favorite: true,
      });
      updateCounterpartyUserState(queryClient, variables, {
        is_favorite: true,
      });
      void queryClient.invalidateQueries({
        queryKey: ["listFavorites"],
      });
    },
  });
};

export const useUnfavoriteUser = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["unfavoriteUser"],
    apiFunction: (userId: string) =>
      FavoriteService.unfavoriteUserApiFavoriteUserIdDelete(userId),
    onSuccess: (_, variables) => {
      updateSearchResultsUserState(queryClient, variables, {
        is_favorite: false,
      });
      updateFavoritedMeResultsUserState(queryClient, variables, {
        is_favorite: false,
      });
      updateCounterpartyUserState(queryClient, variables, {
        is_favorite: false,
      });
      void queryClient.invalidateQueries({
        queryKey: ["listFavorites"],
      });
    },
  });
};

export const useMarkFavoriteSeen = () => {
  return useAuthApiCallMutation({
    mutationKey: ["markFavoriteSeen"],
    apiFunction: (userId: string) =>
      FavoriteService.markFavoriteSeenApiFavoriteUserIdMarkSeenPost(userId),
  });
};

// unreads

export const useUnreads = () => {
  return useAuthApiCallQuery({
    queryKey: ["getUnreads"],
    apiFunction: () => UnreadsService.getUnreadsApiUnreadsGet(),
    refetchInterval: TEN_SECONDS,
    staleTime: TEN_SECONDS,
  });
};

// settings

export const useGetAccountSettings = () => {
  return useAuthApiCallQuery({
    queryKey: ["getAccountSettings"],
    apiFunction: () => SettingsService.accountSettingsApiSettingsGet(),
    gcTime: TWENTY_MINUTES,
    staleTime: FIFTEEN_MINUTES,
    placeholderData: (previousData) => previousData,
  });
};

export const useUpdateAccountSettings = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["updateAccountSettings"],
    apiFunction: (data: UpdateAccountSettings) =>
      SettingsService.updateAccountSettingsApiSettingsPatch(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getAccountSettings"],
      });
    },
  });
};

export const useCreateEmailVerification = () => {
  return useAuthApiCallMutation({
    mutationKey: ["createEmailVerification"],
    apiFunction: (data: EmailPayload) =>
      SettingsService.createEmailVerificationApiSettingsCreateEmailVerificationPost(
        data,
      ),
  });
};

export const useVerifyEmail = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["verifyEmail"],
    apiFunction: (data: EmailOtpCodePayload) =>
      SettingsService.verifyEmailApiSettingsVerifyEmailPost(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getAccountSettings"],
      });
    },
  });
};

export const useAcceptTermsSetLanguage = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["acceptTerms"],
    apiFunction: (data: AcceptTermsPayload) =>
      SettingsService.acceptTermsApiSettingsAcceptTermsPost(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["getAccountSettings"] });
      void queryClient.invalidateQueries({ queryKey: ["getUserProfile"] });
    },
  });
};

// Search

export const useSearchProfiles = () => {
  const queryClient = useQueryClient();
  const { accessToken, setToken, clearToken } = useAuthStore();

  // Store the current search parameters in state but also persist them
  const [currentSearchParams, setCurrentSearchParams] =
    useState<ProfileSearchFilters | null>(() => {
      // Try to get saved params from queryClient state
      const savedParams = queryClient.getQueryData(["searchParams"]);
      return savedParams as ProfileSearchFilters | null;
    });

  const searchQuery = useInfiniteQuery({
    queryKey: ["searchResults", currentSearchParams],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!currentSearchParams) {
        return { items: [], next_cursor: null };
      }
      const result = await refreshOnFailure(
        () =>
          SearchService.searchProfilesApiSearchPost(
            currentSearchParams,
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      );

      // Check if this is the first page (pageParam is null)
      if (!pageParam) {
        void queryClient.invalidateQueries({
          queryKey: ["getRecentSearch"],
        });
      }

      return result;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: !!currentSearchParams,
    gcTime: FIFTEEN_MINUTES,
    staleTime: FIVE_MINUTES,
    // Add these options to help with caching
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const search = useCallback(
    (searchParams: ProfileSearchFilters) => {
      queryClient.removeQueries({ queryKey: ["searchResults"] });
      // Save the search parameters to queryClient state
      queryClient.setQueryData(["searchParams"], searchParams);
      setCurrentSearchParams(searchParams);
    },
    [queryClient],
  );

  return {
    search,
    ...searchQuery,
    data: searchQuery.data?.pages.flatMap((page) => page.items) ?? [],
    hasNextPage: searchQuery.hasNextPage,
    fetchNextPage: searchQuery.fetchNextPage,
    isFetchingNextPage: searchQuery.isFetchingNextPage,
    currentSearchParams, // Expose this if needed
  };
};

export const useRecentSearch = () => {
  return useAuthApiCallQuery({
    queryKey: ["getRecentSearch"],
    apiFunction: () => SearchService.recentSearchApiSearchRecentGet(),
    gcTime: TWENTY_MINUTES,
    staleTime: FIFTEEN_MINUTES,
  });
};

export const useUserActivity = () => {
  return useAuthApiCallMutation({
    mutationKey: ["postUserActivity"],
    apiFunction: () => UserService.logActivityApiUserActivityPost(),
  });
};

const createTempMessage = (
  content: string,
  type: MessageType,
): MessageResponse => ({
  message_id: `temp-${Date.now()}`,
  sender_id: "self_pending",
  message_type: type,
  content: content,
  sent_at: new Date().toISOString(),
  read_at: null,
});

type MessagesContext = {
  previousMessages: InfiniteData<ConversationMessages> | undefined;
};

// messages
export const useSendMessage = (isSupport: boolean) => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation<string, NewMessage, MessagesContext>({
    mutationKey: ["sendMessage"],
    apiFunction: (newMessage: NewMessage) =>
      isSupport
        ? SupportService.sendMessageToSupportApiSupportNewMessagePost(
            newMessage,
          )
        : MessagesService.sendMessageApiMessagesNewPost(newMessage),
    onMutate: async (newMessage) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["listMessages", newMessage.counterparty_id],
      });

      // Get the current messages
      const previousMessages = queryClient.getQueryData<
        InfiniteData<ConversationMessages>
      >(["listMessages", newMessage.counterparty_id]);

      // Create optimistic message
      const optimisticMessage = createTempMessage(
        newMessage.content,
        MessageType.TEXT,
      );

      // Optimistically update the messages cache
      queryClient.setQueryData<InfiniteData<ConversationMessages>>(
        ["listMessages", newMessage.counterparty_id],
        (old) => {
          if (!old || old.pages.length === 0)
            return {
              pages: [
                {
                  items: [optimisticMessage],
                  next_cursor: null,
                },
              ],
              pageParams: [null],
            };

          return {
            ...old,
            pages: (() => {
              if (old.pages.length === 0) {
                return [{ items: [optimisticMessage], next_cursor: null }];
              }
              const [first, ...rest] = old.pages;
              return [
                { ...first, items: [optimisticMessage, ...first.items] },
                ...rest,
              ];
            })(),
          };
        },
      );

      return { previousMessages };
    },
    onError: (_, newMessage, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(
          ["listMessages", newMessage.counterparty_id],
          context.previousMessages,
        );
      }
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["listMessages", variables.counterparty_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["listConversations"],
      });
      updateSearchResultsUserState(queryClient, variables.counterparty_id, {
        has_messaged: true,
      });
      updateFavoriteResultsMessagedState(
        queryClient,
        variables.counterparty_id,
        { has_messaged: true },
      );
      updateFavoritedMeResultsUserState(
        queryClient,
        variables.counterparty_id,
        {
          has_messaged: true,
        },
      );
      updateCounterpartyUserState(queryClient, variables.counterparty_id, {
        has_messaged: true,
      });
    },
  });
};

type ShareContactInput = {
  contactMessage: ContactMessage;
  value: string;
};

const ContactToMessageTypeMap: Record<ContactType, MessageType> = {
  [ContactType.TELEGRAM]: MessageType.TELEGRAM,
  [ContactType.WHATSAPP]: MessageType.WHATSAPP,
  [ContactType.INSTAGRAM]: MessageType.INSTAGRAM,
};

export const useShareContactMessage = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation<string, ShareContactInput, MessagesContext>({
    mutationKey: ["shareContact"],
    apiFunction: ({ contactMessage }) =>
      MessagesService.shareContactApiMessagesShareContactPost(contactMessage),
    onMutate: async ({ contactMessage, value }) => {
      const { counterparty_id, contact_type } = contactMessage;

      await queryClient.cancelQueries({
        queryKey: ["listMessages", counterparty_id],
      });

      const previousMessages = queryClient.getQueryData<
        InfiniteData<ConversationMessages>
      >(["listMessages", counterparty_id]);

      const messageType = ContactToMessageTypeMap[contact_type];
      const optimisticMessage = createTempMessage(value, messageType);

      // Optimistically update the messages cache
      queryClient.setQueryData<InfiniteData<ConversationMessages>>(
        ["listMessages", counterparty_id],
        (old) => {
          if (!old || old.pages.length === 0)
            return {
              pages: [
                {
                  items: [optimisticMessage],
                  next_cursor: null,
                },
              ],
              pageParams: [null],
            };

          return {
            ...old,
            pages: [
              {
                ...old.pages[0],
                items: [optimisticMessage, ...old.pages[0].items],
              },
              ...old.pages.slice(1),
            ],
          };
        },
      );

      return { previousMessages };
    },
    onError: (_, { contactMessage }, context) => {
      const { counterparty_id } = contactMessage;
      if (context?.previousMessages) {
        queryClient.setQueryData(
          ["listMessages", counterparty_id],
          context.previousMessages,
        );
      }
    },
    onSuccess: (_, variables) => {
      const { counterparty_id } = variables.contactMessage;
      void queryClient.invalidateQueries({
        queryKey: ["listMessages", counterparty_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["listConversations"],
      });
      updateSearchResultsUserState(queryClient, counterparty_id, {
        has_messaged: true,
      });
      updateFavoriteResultsMessagedState(queryClient, counterparty_id, {
        has_messaged: true,
      });
      updateFavoritedMeResultsUserState(queryClient, counterparty_id, {
        has_messaged: true,
      });
      updateCounterpartyUserState(queryClient, counterparty_id, {
        has_messaged: true,
      });
    },
  });
};

export const useListConversations = () => {
  const { accessToken, setToken, clearToken } = useAuthStore();
  const [isIdle, setIsIdle] = useState(false);

  useIdleTimer({
    timeout: THREE_MINUTES,
    onIdle: () => setIsIdle(true),
    onActive: () => setIsIdle(false),
  });

  return useInfiniteQuery({
    queryKey: ["listConversations"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          MessagesService.listConversationsApiMessagesConversationsGet(
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    refetchInterval: isIdle ? TEN_SECONDS : FIVE_SECONDS,
  });
};

export const useGetCounterpartyInfo = (
  profileId: string,
  refetch: boolean = false,
) => {
  return useAuthApiCallQueryWithParams({
    queryKey: ["getCounterpartyInfo", profileId],
    apiFunction: (id: string) =>
      MessagesService.getCounterpartyApiMessagesCounterpartyUserIdGet(id),
    params: profileId,
    staleTime: ONE_MINUTE,
    refetchInterval: refetch ? ONE_MINUTE : false,
  });
};

export const useListMessages = (profileId: string, enabled: boolean = true) => {
  const { accessToken, setToken, clearToken } = useAuthStore();
  const [isIdle, setIsIdle] = useState(false);
  const activeSubscriptionQuery = useActiveSubscription();

  const paidStatuses = new Set([
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.ACTIVE_CANCELED,
  ]);

  const isPaid = paidStatuses.has(
    activeSubscriptionQuery.data?.subscription_status ??
      SubscriptionStatus.INACTIVE,
  );

  useIdleTimer({
    timeout: THREE_MINUTES,
    onIdle: () => setIsIdle(true),
    onActive: () => setIsIdle(false),
  });

  return useInfiniteQuery({
    queryKey: ["listMessages", profileId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          MessagesService.listMessagesApiMessagesUserIdGet(
            profileId,
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    refetchInterval: isIdle || !isPaid ? TEN_SECONDS : TWO_SECONDS,
    enabled,
  });
};

export const useMarkMessageRead = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["markMessageRead"],
    apiFunction: (messageId: string) =>
      MessagesService.readMessageApiMessagesReadMessageMessageIdPost(messageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getUnreads"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["listConversations"],
      });
    },
  });
};

export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["deleteConversation"],
    apiFunction: (userId: string) =>
      MessagesService.deleteConversationApiMessagesConversationUserIdDelete(
        userId,
      ),

    onSuccess: (_, userId) => {
      queryClient.setQueryData<InfiniteData<{ items: ConversationResponse[] }>>(
        ["listConversations"],
        (oldData) => {
          if (!oldData) return oldData;

          // Remove the deleted conversation from each page
          const newPages = oldData.pages.map((page) => ({
            ...page,
            items: page.items.filter(
              (conversation) =>
                conversation.counterparty.counterparty_id !== userId,
            ),
          }));

          // If any page becomes empty, we should probably refetch
          if (newPages.some((page) => page.items.length === 0)) {
            // Schedule a refetch in the next tick to avoid React state updates conflicts
            setTimeout(() => {
              void queryClient.invalidateQueries({
                queryKey: ["listConversations"],
              });
            }, 0);
            return oldData; // Return old data while refetching
          }

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );

      updateSearchResultsUserState(queryClient, userId, {
        has_messaged: false,
      });
      updateFavoriteResultsMessagedState(queryClient, userId, {
        has_messaged: false,
      });
      updateFavoritedMeResultsUserState(queryClient, userId, {
        has_messaged: false,
      });
      updateCounterpartyUserState(queryClient, userId, {
        has_messaged: false,
      });
      void queryClient.invalidateQueries({
        queryKey: ["listMessages", userId],
      });
    },
  });
};

export const useDeleteMessage = (counterpartyId: string) => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["deleteConversation"],
    apiFunction: (messageId: string) =>
      MessagesService.deleteMessageApiMessagesMessageMessageIdDelete(messageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["listMessages", counterpartyId],
      });
    },
  });
};

// subscriptions
export const useActiveSubscription = (
  refetch: boolean = false,
  enabled: boolean = true,
) => {
  return useAuthApiCallQuery({
    queryKey: ["getActiveSubscription"],
    apiFunction: () =>
      SubscriptionService.getSubscriptionApiSubscriptionActiveGet(),
    gcTime: THREE_MINUTES,
    staleTime: THREE_MINUTES,
    refetchOnMount: refetch ? "always" : false,
    enabled,
  });
};

export const useSupportProfile = () => {
  return useAuthApiCallQuery({
    queryKey: ["getSupportProfile"],
    apiFunction: () => SupportService.supportProfileApiSupportProfileGet(),
    gcTime: ONE_WEEK,
    staleTime: ONE_WEEK,
  });
};

//  Push notifications
export const usePushSubscribe = () => {
  return useAuthApiCallMutation({
    mutationKey: ["pushSubscribe"],
    apiFunction: (payload: PushSubscriptionPayload) =>
      PushNotificationsService.subscribeApiPushNotificationsSubscribePost(
        payload,
      ),
  });
};

export const usePushUnsubscribe = () => {
  return useAuthApiCallMutation({
    mutationKey: ["pushUnsubscribe"],
    apiFunction: (payload: PushUnsubscribePayload) =>
      PushNotificationsService.unsubscribeApiPushNotificationsUnsubscribePost(
        payload,
      ),
  });
};

// FCM (Capacitor / Firebase Cloud Messaging) push registration.
// Used by iOS now and Android once that app ships. Same token shape on both.
export const useFcmSubscribe = () => {
  return useAuthApiCallMutation({
    mutationKey: ["fcmSubscribe"],
    apiFunction: (payload: FcmPushSubscriptionPayload) =>
      PushNotificationsService.subscribeFcmApiPushNotificationsSubscribeFcmPost(
        payload,
      ),
  });
};

export const useFcmUnsubscribe = () => {
  return useAuthApiCallMutation({
    mutationKey: ["fcmUnsubscribe"],
    apiFunction: (payload: FcmPushSubscriptionPayload) =>
      PushNotificationsService.unsubscribeFcmApiPushNotificationsUnsubscribeFcmPost(
        payload,
      ),
  });
};

// Subscriptions

export const useListSubscriptionPlans = () => {
  return useAuthApiCallQuery({
    queryKey: ["listSubscriptionPlans"],
    apiFunction: () => SubscriptionService.listPlansApiSubscriptionPlansGet(),
    gcTime: THREE_MINUTES,
    staleTime: THREE_MINUTES,
  });
};

export const usePrefetchSubscriptionPlans = () => {
  const { accessToken, setToken, clearToken } = useAuthStore();
  const queryClient = useQueryClient();

  const hasPlansData = queryClient.getQueryData(["listSubscriptionPlans"]);
  if (!hasPlansData) {
    void queryClient.prefetchQuery({
      queryKey: ["listSubscriptionPlans"],
      queryFn: () =>
        refreshOnFailure(
          () => SubscriptionService.listPlansApiSubscriptionPlansGet(),
          accessToken,
          setToken,
          clearToken,
        ),
    });
  }
};

export const useCreateNowpaymentsIntent = () => {
  return useAuthApiCallMutation({
    mutationKey: ["createNowpaymentsIntent"],
    apiFunction: (payload: IntentPayload) =>
      SubscriptionService.createNowpaymentsIntentApiSubscriptionNowpaymentsIntentPost(
        payload,
      ),
  });
};

export const useUpdateNowpaymentStatus = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["updateNowpaymentStatus"],
    apiFunction: (payload: PaymentAttemptPayload) =>
      SubscriptionService.updatePaymentStatusApiSubscriptionNowpaymentStatusPost(
        payload,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getActiveSubscription"],
      });
    },
  });
};

export const useInfiniteReferrals = () => {
  const { accessToken, setToken, clearToken } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ["listReferredUsers"],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      refreshOnFailure(
        () =>
          ReferralsService.listReferralsApiReferralsGet(
            pageParam as string | undefined,
          ),
        accessToken,
        setToken,
        clearToken,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
};

export const useRemainingFemaleReferrals = () => {
  return useAuthApiCallQuery({
    queryKey: ["remainingFemaleReferrals"],
    apiFunction: () =>
      ReferralsService.remainingFemaleSpotsApiReferralsFemaleSpotsGet(),
  });
};

export const useCreateStripeSession = () => {
  return useAuthApiCallMutation({
    mutationKey: ["createStripeSession"],
    apiFunction: (payload: IntentPayload) =>
      SubscriptionService.createStripeSessionApiSubscriptionStripeSessionPost(
        payload,
      ),
  });
};

export const useCheckStripeSessionStatus = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["updateStripeSession"],
    apiFunction: (payload: StripeSessionIdPayload) =>
      SubscriptionService.updateSessionStatusApiSubscriptionStripeSessionStatusPost(
        payload,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getActiveSubscription"],
      });
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useAuthApiCallMutation({
    mutationKey: ["cancelSubscription"],
    apiFunction: (subscriptionId: string) =>
      SubscriptionService.cancelSubscriptionApiSubscriptionCancelSubscriptionIdPost(
        subscriptionId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getActiveSubscription"],
      });
    },
  });
};

export const useSyncRevenueCatStatus = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["syncRevenueCatStatus"],
    apiFunction: () =>
      SubscriptionService.syncFromRevenuecatApiSubscriptionSyncFromRevenuecatPost(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getActiveSubscription"],
      });
    },
  });
};

export const useActivateTrial = () => {
  const queryClient = useQueryClient();

  return useAuthApiCallMutation({
    mutationKey: ["activateTrial"],
    apiFunction: () =>
      SubscriptionService.activateTrialApiSubscriptionActivateTrialPost(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["getActiveSubscription"],
      });
    },
  });
};

export const useAiMatch = () => {
  return useAuthApiCallQuery({
    queryKey: ["aiMatch"],
    apiFunction: () =>
      AiMatchingService.generateMatchApiAiMatchingGeneratePost(),
    // Fetch once on first mount, then never auto-refetch. The Generate
    // button calls refetch() to replace the cached match on demand.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // 4xx failures here are deterministic (429 quota exhausted, 404 no eligible
    // match, 422 missing photos) — retrying just delays the empty state. Only
    // retry transient server/network errors, and only once (generation is an
    // expensive LLM call).
    retry: (failureCount, err) => {
      const status = (err as ApiError | null)?.status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });
};
